<template>
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions vuejs-accessibility/mouse-events-have-key-events -->
    <div class="relative" @mouseleave="closeFlyout">
        <section
            ref="sectionRef"
            class="relative flex items-center h-[60px] px-6 max-w-screen-3xl m-auto"
            role="navigation"
            aria-label="Main navigation"
        >
            <!-- ARIA live region for scroll state announcements -->
            <div
                :aria-live="showLeftArrow || showRightArrow ? 'polite' : 'off'"
                class="sr-only"
            >
                {{ showLeftArrow && showRightArrow ? 'Navigation scrollable in both directions' : showLeftArrow ? 'Scroll left available' : showRightArrow ? 'Scroll right available' : '' }}
            </div>

            <button
                v-show="showLeftArrow"
                type="button"
                class="btn btn-circle btn-ghost btn-sm flex-shrink-0"
                aria-label="Scroll navigation left"
                @click="handleScrollLeft"
            >
                <MiscIcon name="chevron-left" />
            </button>

            <div
                ref="navigationWrapper"
                class="main-navigation overflow-x-auto no-scrollbar"
            >
                <nav ref="navigationList" class="flex flex-row gap-4 flex-nowrap whitespace-nowrap" aria-label="Product categories">
                    <div
                        ref="sentinelStart"
                        data-sentinel="start"
                        class="flex-shrink-0 w-px h-px invisible"
                        aria-hidden="true"
                    />

                    <MiscLink
                        v-for="entry in navigation"
                        :key="entry.id"
                        no-prefetch
                        :to="getCategoryRoute(entry)"
                        active-class="none"
                        :class="{
                            'router-link-active' : isRouteActive(entry),
                        }"
                        class="main-navigation-link h-full flex relative items-center min-h-12"
                        :aria-expanded="hasChildren(entry) ? (entry.id === activeTrigger).toString() : undefined"
                        :aria-haspopup="hasChildren(entry) ? 'true' : undefined"
                        @mouseenter="openFlyoutTimeout(entry.id)"
                        @focus="openFlyoutTimeout(entry.id)"
                        @blur="closeFlyout"
                        @keydown.space.prevent="entry.id === activeTrigger ? closeFlyout() : openFlyout(entry.id, 'keyboard')"
                        @click="closeFlyout"
                    >
                        <span
                            :class="{
                                'text-sale sale' : getCategoryColor(entry) === 'category_color_sale',
                                'text-new new' : getCategoryColor(entry) === 'category_color_new'
                            }"
                            class="leading-tight rounded-[1px] text-sm font-semibold"
                        >
                            {{ getTranslatedProperty(entry, 'name') }}
                        </span>
                    </MiscLink>
                    <div
                        ref="sentinelEnd"
                        data-sentinel="end"
                        class="flex-shrink-0 w-px h-px invisible"
                        aria-hidden="true"
                    />
                </nav>
            </div>

            <button
                v-show="showRightArrow"
                type="button"
                class="btn btn-circle btn-ghost btn-sm flex-shrink-0"
                aria-label="Scroll navigation right"
                @click="handleScrollRight"
            >
                <MiscIcon name="chevron-right" />
            </button>

            <!-- Inline Search Container Slot -->
            <div
                v-show="!useOverlayMode"
                ref="inlineSearchContainer"
                class="flex items-center ml-auto transition-[width] duration-300 ease-in-out"
                :class="{ 'absolute right-6 z-50': searchExpanded }"
                :style="{ width: `${searchExpanded ? 480 : searchWidth}px` }"
            />

            <!-- Search toggle button for overlay mode -->
            <button
                v-show="useOverlayMode"
                type="button"
                class="btn btn-circle btn-ghost py-2 ml-auto xl:hidden"
                :aria-label="$t('layout.header.actions.search')"
                :aria-expanded="searchActive"
                aria-controls="overlay-search-container"
                @click="toggleSearch"
            >
                <MiscIcon name="search" />
            </button>
        </section>

        <!-- Overlay Search Container Slot -->
        <Transition
            enter-active-class="transition-all duration-300 ease-in-out"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-300 ease-in-out"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0"
        >
            <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
            <div
                v-show="useOverlayMode && searchActive"
                id="overlay-search-container"
                ref="overlaySearchContainer"
                class="absolute top-0 left-0 right-0 border-b bg-neutral-content z-[60] shadow-lg"
                role="search"
                aria-label="Site search overlay"
                @keydown.esc.prevent="closeSearch"
            />
        </Transition>

        <!-- Placeholder for inline mode; disabled, cannot be typed into -->
        <Teleport
            v-if="!useOverlayMode && !searchAutocompleteMounted && inlineSearchContainer"
            :to="inlineSearchContainer!"
        >
            <div class="relative w-full py-2 ml-2">
                <div class="relative w-full">
                    <label class="relative block" :aria-label="$t('search.input.label')">
                        <span class="absolute inset-y-0 left-0 flex items-center pl-3 pb-px text-primary z-10">
                            <span v-if="searchAutocompleteReady" class="loading" />
                            <MiscIcon v-else name="search" />
                        </span>
                        <input
                            type="text"
                            class="input block w-full px-10"
                            :placeholder="$t('search.input.placeholder')"
                            :disabled="searchAutocompleteReady"
                            :aria-busy="searchAutocompleteReady"
                            @focusin="onFakeSearchFocus"
                        >
                    </label>
                </div>
            </div>
        </Teleport>

        <!-- Single SearchAutocompleteMeili Component - Teleported based on mode -->
        <Teleport
            v-if="searchAutocompleteReady && (inlineSearchContainer || overlaySearchContainer)"
            :to="useOverlayMode ? overlaySearchContainer! : inlineSearchContainer!"
        >
            <div
                v-show="useOverlayMode || searchAutocompleteMounted"
                :class="[
                    useOverlayMode
                        ? 'max-w-screen-3xl m-auto flex items-center justify-center py-2 px-6 w-full'
                        : 'relative w-full py-2 ml-2'
                ]"
            >
                <div class="relative w-full">
                    <LazySearchAutocompleteMeili
                        ref="searchAutocompleteRef"
                        class="w-full"
                        @focusin="searchFocused = true"
                        @focusout="onSearchFocusOut"
                        @ready="onSearchAutocompleteReady"
                        @results-visible-change="resultsVisible = $event"
                    />
                    <button
                        v-show="(useOverlayMode && searchActive) || searchExpanded"
                        type="button"
                        class="btn btn-sm btn-ghost btn-circle absolute inset-y-0 right-0 m-auto mr-1 z-10 xl:hidden"
                        :aria-label="$t('layout.navigation.horizontal.closeModal')"
                        @click="useOverlayMode ? closeSearch() : closeInlineSearch()"
                        @mousedown.prevent
                        @keydown.esc.prevent="useOverlayMode ? closeSearch() : closeInlineSearch()"
                    >
                        <MiscIcon name="x" />
                    </button>
                </div>
            </div>
        </Teleport>
    </div>

    <!-- eslint-disable-next-line -->
    <div ref="flyoutEl" class="flyout relative" @keydown.esc.prevent="closeFlyout">
        <!-- eslint-disable-next-line -->
        <div
            v-for="(entry, index) in navigation"
            v-show="entry.id === activeTrigger && hasChildren(entry)"
            :key="index"
            class="absolute top-0 left-0 w-full border-b border-t bg-base-100 overflow-hidden z-40 min-h-[410px]"
            @mouseenter="openFlyout(entry.id, 'mouse')"
            @mouseleave="closeFlyout"
        >
            <div class="relative px-6 max-w-screen-3xl m-auto">
                <button class="btn btn-circle absolute top-0 right-6" @click="closeFlyout">
                    <span class="sr-only">{{ $t('layout.navigation.horizontal.closeModal') }}: {{ entry?.name }}</span>
                    <MiscIcon name="x" />
                </button>
            </div>
            <section
                v-if="isBrandCategory(entry.id) && getFlyoutChildren(entry).length"
                class="p-6 max-w-screen-3xl m-auto min-h-[400px] flex items-center"
            >
                <div class="max-w-[900px] m-auto flex flex-wrap justify-between items-center gap-5">
                    <div v-for="(subentry, subChildIndex) in getFlyoutChildren(entry)" :key="subChildIndex">
                        <MiscLink
                            :key="subentry.id"
                            no-prefetch
                            :to="getCategoryRoute(subentry)"
                            active-class="none"
                            class="link no-underline block w-full max-w-[75px]"
                            @click="closeFlyout"
                        >
                            <MiscImage
                                v-if="getBrand(subentry) != null"
                                :src="getMediaUrl(getBrand(subentry)?.media)"
                                :alt="getBrand(subentry)?.media?.translated?.alt"
                                :media-width="getBrand(subentry)?.media?.metaData?.width"
                                :media-height="getBrand(subentry)?.media?.metaData?.height"
                                class="w-full max-h-[50px]"
                                lazy-load
                            />
                            <strong v-else class="text-primary text-sm font-semibold">
                                {{ getTranslatedProperty(subentry, 'name') }}
                            </strong>
                        </MiscLink>
                    </div>

                    <div class="w-full flex justify-center mt-4">
                        <MiscLink :to="getCategoryRoute(entry)" class="btn btn-primary" @click="closeFlyout">
                            {{ $t('layout.navigation.horizontal.allBrands') }}
                        </MiscLink>
                    </div>
                </div>
            </section>
            <!-- Gate on children: only mount once the deep tree has loaded, so the grid
                 column-span is computed from real data (avoids a stale shallow-render layout). -->
            <section v-else-if="getFlyoutChildren(entry).length" class="max-w-screen-3xl m-auto p-6 flex justify-between gap-16">
                <div class="w-full grid grid-cols-6 gap-5">
                    <div
                        class="flex flex-col gap-6"
                        :class="hasHighlightCategories(getFlyoutChildren(entry)) && hasTopBrandCategories(getFlyoutChildren(entry)) ? 'col-span-2' : 'col-span-3'"
                    >
                        <div class="text-primary text-sm font-bold">
                            {{ $t('layout.navigation.horizontal.categories') }}
                        </div>

                        <div
                            class="max-h-auto"
                            :style="`columns: ${hasHighlightCategories(getFlyoutChildren(entry)) && hasTopBrandCategories(getFlyoutChildren(entry)) ? '2' : '3'} auto;`"
                        >
                            <div
                                v-for="subentry in getFlyoutChildren(entry).filter((child) => getFlyoutSection(child) !== 'is_highlight' && getFlyoutSection(child) !== 'is_top_brand' && getFlyoutSection(child) !== 'is_new_sale')"
                                :key="subentry.id"
                                class="inline-block w-full mb-6"
                            >
                                <MiscLink
                                    no-prefetch
                                    :to="getCategoryRoute(subentry)"
                                    active-class="none"
                                    class="link no-underline block w-full text-sm font-semibold"
                                    :class="{'router-link-active' : isRouteActive(subentry)}"
                                    data-cy="flyoutCategoryLink"
                                    @click="closeFlyout"
                                >
                                    <span>{{ getTranslatedProperty(subentry, 'name') }}</span>
                                </MiscLink>

                                <div v-if="getFlyoutChildren(subentry).length" class="pl-4 flex flex-col gap-4 mb-2 mt-4">
                                    <MiscLink
                                        v-for="subSubentry in getFlyoutChildren(subentry)"
                                        :key="subSubentry.id"
                                        no-prefetch
                                        :to="getCategoryRoute(subSubentry)"
                                        active-class="none"
                                        class="navigation-item-link link no-underline text-sm block w-full"
                                        :class="{'router-link-active' : isRouteActive(subSubentry)}"
                                        @click="closeFlyout"
                                    >
                                        {{ getTranslatedProperty(subSubentry, 'name') }}
                                    </MiscLink>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="hasHighlightCategories(getFlyoutChildren(entry))" class="col-span-1 flex flex-col gap-6">
                        <div class="text-primary text-sm font-bold">
                            {{ $t('layout.navigation.horizontal.highlights') }}
                        </div>
                        <div class="flex flex-col gap-6">
                            <MiscLink
                                v-for="subentry in getFlyoutChildren(entry).filter((child) => getFlyoutSection(child) === 'is_highlight' || getFlyoutSection(child) === 'is_new_sale')"
                                :key="subentry.id"
                                no-prefetch
                                :to="getCategoryRoute(subentry)"
                                active-class="none"
                                class="link no-underline block w-full text-sm font-semibold"
                                :class="{'router-link-active' : isRouteActive(subentry)}"
                                @click="closeFlyout"
                            >
                                <span
                                    :class="{
                                        'text-sale sale' : getCategoryColor(subentry) === 'category_color_sale',
                                        'text-new new' : getCategoryColor(subentry) === 'category_color_new'
                                    }"
                                >{{ getTranslatedProperty(subentry, 'name') }}</span>
                            </MiscLink>
                        </div>
                    </div>
                    <div v-if="hasTopBrandCategories(getFlyoutChildren(entry))" class="col-span-1 flex flex-col gap-6">
                        <div class="text-primary text-sm font-bold">
                            {{ $t('layout.navigation.horizontal.topBrands') }}
                        </div>
                        <div class="grid grid-cols-2 gap-2">
                            <MiscLink
                                v-for="subentry in getFlyoutChildren(entry).filter((child) => getFlyoutSection(child) === 'is_top_brand')"
                                :key="subentry.id"
                                no-prefetch
                                :to="getCategoryRoute(subentry)"
                                active-class="none"
                                class="link no-underline block w-full bg-base-200 p-3"
                                @click="closeFlyout"
                            >
                                <MiscImage
                                    v-if="getBrand(subentry)?.media != null"
                                    :src="getMediaUrl(getBrand(subentry)?.media)"
                                    :alt="getBrand(subentry)?.media?.translated?.alt"
                                    :media-width="getBrand(subentry)?.media?.metaData?.width"
                                    :media-height="getBrand(subentry)?.media?.metaData?.height"
                                    class="w-full max-h-[64px]"
                                    lazy-load
                                />
                                <strong v-else class="text-primary text-sm font-semibold">
                                    {{ getTranslatedProperty(subentry, 'name') }}
                                </strong>
                            </MiscLink>
                        </div>
                    </div>
                    <div
                        class="flex flex-col gap-6"
                        :class="hasCmsId(entry) ? 'col-span-2' : 'col-span-3'"
                    >
                        <template v-if="hasCmsId(entry) && uniqueSections.get(entry.id) === getCmsId(entry)">
                            <div v-if="!cmsPending && campaignContent[getCmsId(entry) as string] != null" class="hidden w-full md:block">
                                <StructurePage
                                    v-if="campaignContent[getCmsId(entry) as string]"
                                    :key="getCmsId(entry)"
                                    :content="campaignContent[getCmsId(entry) as string]!"
                                />
                            </div>
                            <div v-else class="hidden w-full shrink-0 h-[410px] md:grid grid-cols-2 gap-4">
                                <div class="w-full bg-base-300 h-full" />
                                <div class="w-full bg-base-300 h-full" />
                                <div class="w-full bg-base-300 h-full" />
                                <div class="w-full bg-base-300 h-full" />
                            </div>
                        </template>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { useResizeObserver, useDebounceFn } from '@vueuse/core'
import { getCategoryRoute, getTranslatedProperty, getCategoryBreadcrumbs } from '@shopware/helpers'

const SEARCH_MAX_WIDTH = 480
const SEARCH_MIN_WIDTH = 200
const GAPS = 48
const ARROW_BUTTON_WIDTH = 32

const props = defineProps<{
    navigation: Schemas['NavigationRouteResponse']
}>()

const {
    navigationWrapper,
    navigationList,
    showLeftArrow,
    showRightArrow,
    handleScrollLeft,
    handleScrollRight
} = useNavigationScroll()

const { apiClient } = useShopwareContext()
const { breadcrumbs } = useBreadcrumbs()

// Deep category tree is fetched client-side on first interaction (see layouts/default.vue).
// The 500ms flyout-open delay overlaps the fetch so the tree is usually ready on open.
const loadMainNavigation = inject<() => void>('loadMainNavigation', () => {})
const sectionRef = ref<HTMLElement | null>(null)
const useOverlayMode = ref(true)
const searchWidth = ref(SEARCH_MAX_WIDTH)
const isMounted = ref(false)

const activeTrigger = ref()
const cmsPending = ref(false)
const searchActive = ref(false)
const searchFocused = ref(false)
// True while the autocomplete popup shows results. Kept separate from `searchFocused` so
// the inline search stays expanded after the input blurs as long as results are visible —
// otherwise the container (and the still-open popup inside it) shrinks mid-interaction.
const resultsVisible = ref(false)
const searchExpanded = computed(() => searchFocused.value || resultsVisible.value)
const searchAutocompleteRef = ref()
const inlineSearchContainer = ref<HTMLElement | null>(null)
const overlaySearchContainer = ref<HTMLElement | null>(null)
const searchAutocompleteReady = ref(false)

// Lazy-load the search autocomplete only once the user wants to search
const searchAutocompleteMounted = ref(false)

const onFakeSearchFocus = () => {
    searchFocused.value = true
    searchAutocompleteReady.value = true
}

const onSearchAutocompleteReady = () => {
    searchAutocompleteMounted.value = true
    if (useOverlayMode.value && !searchActive.value) return // overlay was closed again while loading
    nextTick(() => searchAutocompleteRef.value?.focus())
}

const updateLayoutMode = () => {
    if (!sectionRef.value || !navigationList.value || !navigationWrapper.value) return

    // calculate space: total width -> nav items -> margins and scroll arrows (if needed)
    const navContentWidth = navigationList.value.scrollWidth
    const needsScrollArrows = navContentWidth > navigationWrapper.value.clientWidth
    const arrowsSpace = needsScrollArrows ? ARROW_BUTTON_WIDTH * 2 : 0
    const availableSpaceForSearch = sectionRef.value.clientWidth
        - navContentWidth
        - GAPS
        - arrowsSpace

    // Determine search mode: inline (if space available) or overlay
    if (availableSpaceForSearch >= SEARCH_MAX_WIDTH) {
        // Full-width inline search
        useOverlayMode.value = false
        searchWidth.value = SEARCH_MAX_WIDTH
    } else if (availableSpaceForSearch >= SEARCH_MIN_WIDTH) {
        // Compressed inline search
        useOverlayMode.value = false
        searchWidth.value = availableSpaceForSearch
    } else {
        // Not enough space - use overlay mode
        useOverlayMode.value = true
        searchWidth.value = SEARCH_MIN_WIDTH
    }
}

// Debounce layout calculations to prevent duplicate expensive calculations on resize
const debouncedUpdateLayout = useDebounceFn(updateLayoutMode, 100)

useResizeObserver(sectionRef, debouncedUpdateLayout)
useResizeObserver(navigationWrapper, debouncedUpdateLayout)

// Initial layout calculation on mount
onMounted(() => {
    isMounted.value = true

    nextTick(() => {
        updateLayoutMode()
    })
})

// Close search overlay when switching to inline mode
watch(useOverlayMode, (isOverlay) => {
    if (!isOverlay && searchActive.value) {
        searchActive.value = false
    }
})

watch(searchActive, (isActive) => {
    if (isActive && searchAutocompleteMounted.value) {
        nextTick(() => {
            searchAutocompleteRef.value?.focus()
        })
    }
})

const toggleSearch = () => {
    closeFlyout()
    searchAutocompleteReady.value = true
    searchActive.value = !searchActive.value
}

const closeSearch = () => {
    searchActive.value = false
}

const closeInlineSearch = () => {
    searchAutocompleteRef.value?.clear()
    searchFocused.value = false
}

const onSearchFocusOut = (event: FocusEvent) => {
    // Don't close if focus moves to the close button
    const relatedTarget = event.relatedTarget as HTMLElement
    if (relatedTarget?.closest('[ref="searchContainerRef"]')) {
        return
    }
    searchFocused.value = false
}

const flyoutEl = ref()
let flyoutTimeout: ReturnType<typeof setTimeout>
const { activate: activateFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap(flyoutEl)

const openFlyoutTimeout = (trigger: string) => {
    loadMainNavigation()
    clearTimeout(flyoutTimeout)

    flyoutTimeout = setTimeout(() => {
        activeTrigger.value = trigger
    }, 500)
}

const openFlyout = (trigger: string, event: 'keyboard' | 'mouse') => {
    clearTimeout(flyoutTimeout)
    activeTrigger.value = trigger

    if (event === 'keyboard') {
        nextTick(() => {
            activateFocusTrap()
        })
    }
}

const closeFlyout = () => {
    clearTimeout(flyoutTimeout)
    activeTrigger.value = false

    nextTick(() => {
        deactivateFocusTrap()
    })
}

const uniqueSections = new Map<string, string>()
const campaignContent = ref<Record<string, Schemas["CmsPage"] | null>>({})
watch(activeTrigger, async (value) => {
    if (value !== false && Object.keys(campaignContent.value).length === 0 && !cmsPending.value) {
        await fetchAllCmsData()
    }
})

// childCount so the top-level bar exposes its flyout affordance at SSR (depth-0 fetch has
// no children yet); the deep tree fills entry.children in client-side afterwards.
// Once loaded, only children shown in the flyout count (see isCategoryShownInFlyout).
const hasChildren = (item: Schemas['Category']): boolean => {
    if (item?.children?.length) {
        return getFlyoutChildren(item).length > 0
    }

    return (item?.childCount ?? 0) > 0
}

// Prefix match: nav item is active if the current page is anywhere under it.
// startIndex: 1 skips "Home" — compare by name only to work on both nav and product pages.
const isRouteActive = (item: Schemas['Category']): boolean => {
    if (!isMounted.value || !breadcrumbs.value?.length) return false
    const catNames = getCategoryBreadcrumbs(item, { startIndex: 1 }).map(b => b.name)
    const currentNames = breadcrumbs.value.map(b => b.name)
    return catNames.length > 0 && JSON.stringify(currentNames.slice(0, catNames.length)) === JSON.stringify(catNames)
}

const isBrandCategory = (id: string): boolean => {
    return id === '0190e536e65e77f7b8baa35d2d5879aa'
}

const getMediaUrl = (media: Schemas['Media'] | null | undefined): string => {
    if (!media) {
        return '/'
    }

    return media.path
}

const fetchAllCmsData = async () => {
    if (props?.navigation) {
        for (const entry of props.navigation) {
            const cmsId = hasCmsId(entry) ? getCmsId(entry) : undefined
            if (cmsId != null && cmsId.trim() != '') {
                if (!uniqueSections.has(entry.id)) {
                    uniqueSections.set(entry.id, cmsId)
                }

                campaignContent.value[cmsId] = await fetchCms(cmsId)
            }
        }
    }
}

const fetchCms = async (cmsId: string) => {
    cmsPending.value = true

    try {
        const { data } = await apiClient.invoke('readCms post /cms/{id}', {
            pathParams: {
                id: cmsId
            }, 
            body: {
                associations: {
                    media: {}
                }
            }
        })

        return data as Schemas["CmsPage"]
    } catch (error) {
        console.error(error)
        return null
    } finally {
        cmsPending.value = false
    }
}

const hasCmsId = (entry: Schemas['Category']): boolean => {
    const cmsId = (getTranslatedProperty(entry, 'customFields') as Schemas['Category']['translated']['customFields'])?.custom_cms_id
    return cmsId !== undefined && cmsId !== null && cmsId !== ''
}

const getCmsId = (entry: Schemas['Category']): string | undefined => {
    return (getTranslatedProperty(entry, 'customFields') as Schemas['Category']['translated']['customFields'])?.custom_cms_id ?? undefined
}

const getCategoryColor = (entry: Schemas['Category']) => {
    return (getTranslatedProperty(entry, 'customFields') as Schemas['Category']['translated']['customFields'])?.custom_category_navigation_color
}

const getFlyoutSection = (entry: Schemas['Category']) => {
    return (getTranslatedProperty(entry, 'customFields') as Schemas['Category']['translated']['customFields'])?.custom_category_flyout_section
}

const getBrand = (entry: Schemas['Category']) => {
    return entry.extensions?.a4g_brand
}

const hasHighlightCategories = (children: Schemas['Category'][]) => {
    return children?.some((child) => {
        const section = getFlyoutSection(child)
        return section === 'is_highlight' || section === 'is_new_sale'
    })
}

const hasTopBrandCategories = (children: Schemas['Category'][]) => {
    return children?.some((child) => getFlyoutSection(child) === 'is_top_brand')
}
</script>
